/**
 * Centralized share deep-link builders. Extend by adding cases and platform ids.
 */
export const SHARE_LINK_PLATFORM_IDS = ['facebook', 'whatsapp', 'reddit', 'x'] as const;
export type ShareLinkPlatformId = (typeof SHARE_LINK_PLATFORM_IDS)[number];

export type SocialShareLinkParams = {
  /** Canonical absolute article URL */
  url: string;
  /** Article headline (may be empty; callers should normalize) */
  title: string;
};

function assertHttpUrl(url: string): void {
  if (!url || (!url.startsWith('https://') && !url.startsWith('http://'))) {
    throw new Error(`socialShareUrls: expected absolute http(s) URL, got "${url}"`);
  }
}

/**
 * Returns a ready-to-open share URL for the given platform.
 * All query components are correctly encoded.
 */
export function buildSocialShareUrl(platform: ShareLinkPlatformId, params: SocialShareLinkParams): string {
  const { url, title } = params;
  assertHttpUrl(url);
  const safeTitle = title.trim();
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(safeTitle);
  const text = encodeURIComponent(safeTitle ? `${safeTitle}\n${url}` : url);

  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'whatsapp':
      return `https://wa.me/?text=${text}`;
    case 'reddit':
      return `https://www.reddit.com/submit?url=${u}&title=${t}`;
    case 'x':
      return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    default:
      return url;
  }
}

/* Extensibility: add e.g. 'linkedin' | 'telegram' to SHARE_LINK_PLATFORM_IDS, extend switch above,
   then register glyph + i18n key in ArticleShareBar. For navigator.share(), add a separate
   code path in the bar component (feature-detect, same analytics payload). */
